import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const Datenschutz = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="datenschutz-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#7C9082] hover:text-[#6A7D70] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Zurück zur Startseite</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-[#2C3329] mb-8">
            Datenschutzerklärung
          </h1>

          <div className="prose prose-lg max-w-none text-[#5C6658]">
            {/* Einleitung */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                1. Datenschutz auf einen Blick
              </h2>
              
              <h3 className="text-xl font-medium text-[#2C3329] mt-6 mb-3">
                Allgemeine Hinweise
              </h3>
              <p>
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. 
                Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter 
                diesem Text aufgeführten Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-medium text-[#2C3329] mt-6 mb-3">
                Datenerfassung auf dieser Website
              </h3>
              <p>
                <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              </p>
              <p className="mt-4">
                <strong>Wie erfassen wir Ihre Daten?</strong><br />
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
                Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>
              <p className="mt-4">
                Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der 
                Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten 
                (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
              </p>
            </section>

            {/* Verantwortliche Stelle */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                2. Verantwortliche Stelle
              </h2>
              <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
                <p>
                  <strong>DekoRent</strong><br />
                  Karen Tappe<br />
                  Hessenring 21<br />
                  34246 Vellmar<br /><br />
                  Telefon: 0176 34594053<br />
                  E-Mail: info@dekorent-vellmar.de
                </p>
              </div>
              <p className="mt-4">
                Verantwortliche Stelle ist die natürliche oder juristische Person, die allein 
                oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von 
                personenbezogenen Daten (z.B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
              </p>
            </section>

            {/* Datenerfassung */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                3. Datenerfassung auf dieser Website
              </h2>

              <h3 className="text-xl font-medium text-[#2C3329] mt-6 mb-3">
                Kontaktformular
              </h3>
              <p>
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben 
                aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten 
                zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns 
                gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p className="mt-4">
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, 
                sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur 
                Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen 
                beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven 
                Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder 
                auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).
              </p>
              <p className="mt-4">
                Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie 
                uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder 
                der Zweck für die Datenspeicherung entfällt. Zwingende gesetzliche Bestimmungen – 
                insbesondere Aufbewahrungsfristen – bleiben unberührt.
              </p>

              <h3 className="text-xl font-medium text-[#2C3329] mt-6 mb-3">
                Welche Daten werden erfasst?
              </h3>
              <p>Im Kontaktformular werden folgende Daten erfasst:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Name (Pflichtfeld)</li>
                <li>E-Mail-Adresse (Pflichtfeld)</li>
                <li>Telefonnummer (freiwillig)</li>
                <li>Art des Anlasses (Hochzeit, Geburtstag, Taufe, Jubiläum, Sonstiges)</li>
                <li>Datum/Zeitraum der Feier</li>
                <li>Ort der Feier</li>
                <li>Ihre Nachricht</li>
              </ul>
            </section>

            {/* Server-Log-Dateien */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                4. Server-Log-Dateien
              </h2>
              <p>
                Der Provider der Seiten erhebt und speichert automatisch Informationen in so 
                genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Browsertyp und Browserversion</li>
                <li>verwendetes Betriebssystem</li>
                <li>Referrer URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse</li>
              </ul>
              <p className="mt-4">
                Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.
              </p>
            </section>

            {/* Ihre Rechte */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                5. Ihre Rechte
              </h2>
              <p>Sie haben jederzeit das Recht:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>
                  <strong>Auskunft</strong> über Ihre bei uns gespeicherten personenbezogenen 
                  Daten zu erhalten (Art. 15 DSGVO)
                </li>
                <li>
                  <strong>Berichtigung</strong> unrichtiger personenbezogener Daten zu verlangen 
                  (Art. 16 DSGVO)
                </li>
                <li>
                  <strong>Löschung</strong> Ihrer bei uns gespeicherten personenbezogenen Daten 
                  zu verlangen (Art. 17 DSGVO)
                </li>
                <li>
                  <strong>Einschränkung</strong> der Verarbeitung Ihrer personenbezogenen Daten 
                  zu verlangen (Art. 18 DSGVO)
                </li>
                <li>
                  <strong>Widerspruch</strong> gegen die Verarbeitung einzulegen (Art. 21 DSGVO)
                </li>
                <li>
                  <strong>Datenübertragbarkeit</strong> – Ihre Daten in einem strukturierten, 
                  gängigen Format zu erhalten (Art. 20 DSGVO)
                </li>
              </ul>
            </section>

            {/* Widerruf */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                6. Widerruf Ihrer Einwilligung
              </h2>
              <p>
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung 
                möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. 
                Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom 
                Widerruf unberührt.
              </p>
            </section>

            {/* Beschwerderecht */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                7. Beschwerderecht bei der Aufsichtsbehörde
              </h2>
              <p>
                Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht 
                bei einer Aufsichtsbehörde zu. Das Beschwerderecht besteht unbeschadet anderweitiger 
                verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
              </p>
              <p className="mt-4">
                <strong>Zuständige Aufsichtsbehörde:</strong><br />
                Der Hessische Beauftragte für Datenschutz und Informationsfreiheit<br />
                Postfach 3163<br />
                65021 Wiesbaden<br />
                Telefon: 0611 / 1408-0<br />
                E-Mail: poststelle@datenschutz.hessen.de
              </p>
            </section>

            {/* SSL/TLS */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                8. SSL- bzw. TLS-Verschlüsselung
              </h2>
              <p>
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung 
                vertraulicher Inhalte, wie zum Beispiel Anfragen, die Sie an uns als 
                Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte 
                Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" 
                auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
              </p>
            </section>

            {/* Speicherdauer */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                9. Speicherdauer
              </h2>
              <p>
                Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer 
                genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck 
                für die Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschersuchen 
                geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, werden 
                Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für 
                die Speicherung Ihrer personenbezogenen Daten haben.
              </p>
            </section>

            {/* Aktualität */}
            <section className="mb-10">
              <h2 className="text-2xl font-medium text-[#2C3329] mb-4">
                10. Aktualität und Änderung dieser Datenschutzerklärung
              </h2>
              <p>
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}.
              </p>
              <p className="mt-4">
                Durch die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher 
                bzw. behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung 
                zu ändern.
              </p>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#2C3329] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#8C9688] text-sm">
            © {new Date().getFullYear()} DekoRent - Karen Tappe
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Datenschutz;
